import React, { useState } from 'react';
import axios from 'axios';

import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Heading from '~/components/core/TextComponents/Heading';
import { AddIcon } from '~/components/icons';
import useOrganization from '~/components/OrganizationContext';
import CategoriesTable from '~/components/SystemConfiguration/NotificationsConfiguration/CategoriesTable';
import AddEditCategoryDialog from '~/components/SystemConfiguration/NotificationsConfiguration/EditCateogryDialog';
import type { CategoryModel } from '~/components/SystemConfiguration/NotificationsConfiguration/NotificationsCategories';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

const getCategoriesRoute = (organizationId: number) =>
  `/sysconfig/api/v1/organizations/${organizationId}/communication_categories`;

const CommunicationCategoriesSection: React.FC = () => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization: sysconfigOrganization } = useSysconfig();
  // @ts-expect-error TODO: useOrganization hook needs TypeScript definition
  const { reloadCommunicationCategories } = useOrganization();

  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [categoryToUpdate, setCategoryToUpdate] = useState<CategoryModel | undefined>(undefined);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);

  const {
    isError: isCategoriesError,
    isLoading: isCategoriesLoading,
    data: categories,
    reloadData: reloadCategoriesData,
  } = useDataFetcher<CategoryModel[]>(getCategoriesRoute(sysconfigOrganization.id));

  const handleEditClicked = (category: CategoryModel) => {
    setShowAddEditDialog(true);
    setCategoryToUpdate(category);
  };

  const handleAddEdit = async (category: Partial<CategoryModel>) => {
    try {
      setIsUpdatingCategory(true);
      if (category.id) {
        await axios.patch(`${getCategoriesRoute(sysconfigOrganization.id)}/${category.id}`, category);
      } else {
        await axios.post(getCategoriesRoute(sysconfigOrganization.id), category);
      }
      await reloadCategoriesData();
      await reloadCommunicationCategories();
      setCategoryToUpdate(undefined);
      setShowAddEditDialog(false);
    } catch (err) {
      await reportAxiosError(err);
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const handleToggleDisabled = async (categoryId: number, isDisabled: boolean) => {
    await handleAddEdit({ id: categoryId, is_disabled: isDisabled });
  };

  return (
    <>
      <Heading variant={Heading.TYPES.H3}>Categories</Heading>
      <Text weight="regular" variant="small" className="mt-10">
        Define labels you can assign to communications for easier filtering and organization. Categories appear on
        communications and can be used in search and filters.
      </Text>
      <div className="mt-20 flex h-full flex-row items-end justify-end pb-20">
        <Button
          color="primary"
          onClick={() => setShowAddEditDialog(true)}
          disabled={isUpdatingCategory || isCategoriesError || isCategoriesLoading}
        >
          <AddIcon iconColor="currentColor" />
          Add Category
        </Button>
      </div>
      <CategoriesTable
        categories={categories || []}
        isLoading={isCategoriesLoading}
        isError={isCategoriesError}
        isUpdating={isUpdatingCategory}
        handleEditClicked={handleEditClicked}
        handleToggleDisabled={handleToggleDisabled}
      />
      {showAddEditDialog && (
        <AddEditCategoryDialog
          category={categoryToUpdate}
          onCancel={() => {
            setCategoryToUpdate(undefined);
            setShowAddEditDialog(false);
          }}
          onSubmit={handleAddEdit}
          existingKeys={(categories || []).map(({ key }: { key: string }) => key)}
          showDescription
        />
      )}
    </>
  );
};

export default CommunicationCategoriesSection;
